import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CourtDetailScreenProps } from './types';
import { Image } from '../../components/Image';
import { STYLE_VARS } from '../../constants/style-vars';
import { Button } from '../../components/Button';
import { Footer } from '../../components/Footer';
import dayjs from 'dayjs';
import CourtBooking from './components/CourtBooking';
import { fakeData } from './constants';
import Coaching from '../Sports/components/Coaching';
import CalendarPicker from 'react-native-calendar-picker';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import SportsLayout from '../Sports/components/SportsLayout';
import HeaderCourts from './components/HeaderCourts';
import DatePicker from '../../components/DatePicker';
import { COLORS, FONTS } from '../../constants';
import { NavigationService } from '../../services/navigation';
import { ICourt } from '../../models/ICourt';
import SportApi from '../../services/api/sports.api';
import { debounce } from 'lodash';
import { IFacilities } from '../../types/sports.types';
import { Loading } from '../../components';
import { useSelector } from 'react-redux';
import { RootState } from '../../services/redux';
import { setHeaderConfigAxios } from '../../services/api/axios';
import { IS_WEB } from '../../constants/platform';

const CourtDetail: FC<CourtDetailScreenProps> = ({ navigation, route }) => {
  const sheet = route.params.sheet;
  const courtParam = route.params.court;
  const [court, setCourt] = useState<ICourt>();
  const [isLoadingCourt, setIsLoadingCourt] = useState<boolean>(false);
  const [isChangingDate, setIsChangingDate] = useState<boolean>(false);
  const [facility, setFacility] = useState<IFacilities>();
  const isDesktop = useIsDesktop();
  const [navDay, setNavDay] = useState(dayjs(new Date()).format('YYYY-MM-DD'));
  const auth = useSelector((redux: RootState) => redux.authReducer);

  const getCourt = useCallback(
    async (date: string) => {
      setIsLoadingCourt(true);
      setHeaderConfigAxios(auth.token);
      SportApi.getSport({ date, sheet, court: courtParam })
        .then((res) => {
          setCourt(res.data);
          const currentFacility = res.data.facilities.filter((item) => {
            if (courtParam) {
              return item.bs_no === sheet && item.fac_ecode === courtParam;
            } else {
              return item.bs_no === sheet;
            }
          });
          setFacility(currentFacility[0]);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsLoadingCourt(false);
        });
    },
    [sheet, auth.token, courtParam]
  );

  const handleChangeDate = useCallback(
    async (date: string) => {
      setIsChangingDate(true);
      const filterDate = dayjs(date).format('YYYYMMDD');

      SportApi.getSport({ date: filterDate, sheet, court: courtParam })
        .then((res) => {
          setCourt(res.data);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setIsChangingDate(false);
        });
    },
    [sheet, courtParam]
  );

  const debouncedChangeDate = useMemo(() => {
    return debounce(handleChangeDate, 500);
  }, [handleChangeDate]);

  const renderExplain = useMemo(() => {
    const explains = [
      {
        title: 'Full',
        content: 'TIME SLOT HAS BEEN BOOKED BY ANOTHER MEMBER.',
      },
      {
        title: 'Booked',
        content: 'TIME SLOT HAS BEEN BOOKED BY YOURSELF.',
      },
      {
        title: 'Available',
        content:
          'TIME SLOT IS AVAILABLE FOR BOOKING ON A FIRST COME FIRST SERVE BASIS.',
      },
    ];
    return explains.map((explain) => (
      <View style={{ flexDirection: 'row' }} key={explain.title}>
        <Text style={[styles.explainText, { color: STYLE_VARS.textColor }]}>
          {explain.title} -{' '}
        </Text>
        <Text style={[styles.explainText, { flex: 1 }]}>{explain.content}</Text>
      </View>
    ));
  }, []);

  const title = useMemo(() => {
    return (
      <View
        style={{
          marginBottom: 20,
          alignItems: 'center',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {!IS_WEB && (
            <TouchableOpacity
              style={{
                padding: 5,
                paddingRight: 20,
              }}
              onPress={() => {
                NavigationService.pop();
              }}
            >
              <Image
                source={require('../../assets/left-arrow.png')}
                style={{
                  height: 14,
                  width: 10,
                  resizeMode: 'contain',
                  tintColor: STYLE_VARS.primaryColor,
                }}
              />
            </TouchableOpacity>
          )}

          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Image
              source={require('../../assets/tennis-sport-icon.png')}
              style={{
                width: 20,
                height: 20,
                resizeMode: 'contain',
                marginRight: 10,
              }}
            />
            <Text
              style={{
                fontSize: isDesktop() ? 20 : 18,
                letterSpacing: 2,
                fontFamily: FONTS.IowanOldStyleBTRoman,
                color: STYLE_VARS.textColor,
                fontWeight: '500',
                textTransform: 'uppercase',
              }}
            >
              {facility?.bs_desc} {sheet === 'T' && 'Courts'}
            </Text>
          </View>
        </View>
        {IS_WEB && (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center' }}
            onPress={() => {
              navigation.pop();
            }}
          >
            <Text
              style={{
                fontSize: isDesktop() ? 14 : 12,
                fontFamily: FONTS.AvenirMedium,
                color: STYLE_VARS.textColor,
                fontWeight: '500',
                marginRight: 5,
              }}
            >
              BACK
            </Text>
            <Image
              source={require('../../assets/sport-back-button.png')}
              style={{
                width: 16,
                height: 16,
                resizeMode: 'contain',
                marginRight: 5,
              }}
            />
          </TouchableOpacity>
        )}
      </View>
    );
  }, [isDesktop, navigation, facility, sheet]);

  useEffect(() => {
    const currentDate = dayjs(new Date()).format('YYYYMMDD');
    getCourt(currentDate);
  }, [getCourt]);

  if (isDesktop()) {
    return (
      <SportsLayout
        current="Facility"
        title={`Facility booking > ${facility?.bs_desc || ''}`}
      >
        {isLoadingCourt === true ? (
          <Loading />
        ) : (
          <>
            {title}
            <HeaderCourts facility={facility} />
            <View
              style={{
                paddingHorizontal: 20,
                flexDirection: 'row',
                zIndex: 100,
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 30,
              }}
            >
              <DatePicker
                date={navDay}
                onChangeDate={(date) => {
                  setNavDay(date);
                  debouncedChangeDate(date);
                }}
                haveCalendar={true}
              />
            </View>
            {isChangingDate === true ? (
              <View style={{ marginTop: 20 }}>
                <Loading />
              </View>
            ) : court ? (
              <CourtBooking
                data={court}
                navDay={navDay}
                handleChangeDate={handleChangeDate}
                facility={facility}
              />
            ) : (
              <View style={styles.noDataWrapper}>
                <Text style={[styles.noDataText, { fontSize: 16 }]}>
                  THERE ARE CURRENTLY NO NEWS
                </Text>
              </View>
            )}
          </>
        )}
      </SportsLayout>
    );
  }

  return (
    <>
      <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
        {isLoadingCourt === true ? (
          <View style={{ marginVertical: 40 }}>
            <Loading />
          </View>
        ) : (
          <>
            <View style={styles.container}>
              <View style={{ marginTop: 20 }}>{title}</View>
            </View>
            <HeaderCourts facility={facility} />
            <View
              style={{
                paddingHorizontal: 20,
                zIndex: 100,
                justifyContent: 'center',
                flexDirection: 'row',
              }}
            >
              <DatePicker
                date={navDay}
                onChangeDate={(date) => {
                  setNavDay(date);
                  debouncedChangeDate(date);
                }}
                haveCalendar={true}
              />
            </View>
            {isChangingDate === true ? (
              <View style={{ marginTop: 10, marginBottom: 20 }}>
                <Loading />
              </View>
            ) : court ? (
              <CourtBooking
                data={court}
                navDay={navDay}
                handleChangeDate={handleChangeDate}
                facility={facility}
              />
            ) : (
              <View style={styles.noDataWrapper}>
                <Text style={[styles.noDataText, { fontSize: 18 }]}>
                  THERE ARE CURRENTLY NO SLOT COURT
                </Text>
              </View>
            )}
            <View style={[styles.container, { flexDirection: 'column' }]}>
              <Text style={styles.explainText}>
                MEMBERS ARE ALLOWED TO BOOK THE SAME FACILITY ONCE PER DAY.
              </Text>
              {renderExplain}
            </View>
            <View style={styles.container}>
              <Coaching
                onPressCoaching={() => {
                  NavigationService.push('Root', {
                    screen: 'MenuTab',
                    params: {
                      screen: 'Sports',
                      params: {
                        screen: 'Coaching',
                      },
                    },
                  });
                }}
              />
            </View>
          </>
        )}
        <Footer />
      </ScrollView>
    </>
  );
};

export default CourtDetail;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  arrowButton: {
    padding: 5,
  },
  arrowStyle: {
    height: 13,
    width: 10,
    resizeMode: 'contain',
    tintColor: COLORS.GREEN,
  },
  explainText: {
    color: STYLE_VARS.fccDarkerGrey,
    fontWeight: '500',
    fontFamily: FONTS.AvenirMedium,
    fontSize: 11,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  noDataWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  noDataText: {
    color: COLORS.GREEN,
    textTransform: 'uppercase',
    fontFamily: FONTS.AvenirMedium,
  },
});
